import React, { useState, useEffect, useMemo } from 'react';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  ThemeProvider,
  createTheme,
  Chip,
  Grid,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import documentEditorService from '../../services/documentEditor.service';
import { useProject } from '../../context/ProjectContext';
import {
  getIcfVersionDisplayLabel,
  getIcfVersionRowKey,
  sortIcfVersionsNewestFirst,
} from '../utils/icfVersionUtils';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  typography: { fontFamily: '"Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif' },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } } },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 2px 4px rgba(0,0,0,0.05)' } } },
  },
});

const ICFSectionHistory = () => {
  const { id: sectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();

  const actualProjectId = currentProject?._id || projectIdFromUrl;

  const [section, setSection] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionKey, setSelectedVersionKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const sortedVersions = useMemo(
    () => sortIcfVersionsNewestFirst(versions),
    [versions],
  );

  const selectedVersion = useMemo(() => {
    if (!sortedVersions.length) return null;
    const key = selectedVersionKey || getIcfVersionRowKey(sortedVersions[0], 0);
    return sortedVersions.find((v, idx) => getIcfVersionRowKey(v, idx) === key) || sortedVersions[0];
  }, [sortedVersions, selectedVersionKey]);

  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);

  useEffect(() => {
    if (sectionId && actualProjectId && !projectContextLoading) {
      fetchSectionAndVersions();
    } else if (!sectionId && !actualProjectId && !projectContextLoading) {
      setError('No section ID or project selected.');
      setIsLoading(false);
    } else if (sectionId && !actualProjectId && !projectContextLoading) {
      setError('Please select a project to view its ICF section history.');
      setIsLoading(false);
    }
  }, [sectionId, actualProjectId, projectContextLoading]);

  const fetchSectionAndVersions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const sectionData = await documentEditorService.getICFSection(sectionId, actualProjectId);
      setSection(sectionData.document || sectionData);

      const versionsData = await documentEditorService.getAllICFVersions(sectionId, actualProjectId);
      const loaded = versionsData.versions || [];
      setVersions(loaded);
      const sorted = sortIcfVersionsNewestFirst(loaded);
      if (sorted.length > 0) {
        setSelectedVersionKey(getIcfVersionRowKey(sorted[0], 0));
      }
    } catch (err) {
      console.error('Error fetching ICF section history:', err);
      setError(err.message || 'Failed to load ICF section history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/document-editor/icf/${actualProjectId}?projectId=${actualProjectId}`);
  };

  if (isLoading || projectContextLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (error || projectContextError) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3 }}>
          <Typography color="error">{error || projectContextError}</Typography>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Editor
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  if (!section || !sectionId || !actualProjectId) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3 }}>
          <Typography color="warning">No section or project selected for history.</Typography>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Editor
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBack}
          sx={{ mb: 3 }}
        >
          Back to ICF Editor
        </Button>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {section?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sortedVersions.length} version{sortedVersions.length === 1 ? '' : 's'} saved
          </Typography>
        </Paper>

        {sortedVersions.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
              No version history available for this ICF section.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 1 }}>
                <List dense>
                  {sortedVersions.map((version, idx) => {
                    const rowKey = getIcfVersionRowKey(version, idx);
                    const isSelected = selectedVersionKey === rowKey;
                    return (
                      <React.Fragment key={rowKey}>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => setSelectedVersionKey(rowKey)}
                        >
                          <ListItemText
                            primary={(
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getIcfVersionDisplayLabel(version)}
                                {idx === 0 && (
                                  <Chip label="Latest" size="small" color="primary" variant="outlined" />
                                )}
                              </Box>
                            )}
                            secondary={version.createdAt
                              ? new Date(version.createdAt).toLocaleString()
                              : `Section version ${version.versionNumber ?? '—'}`}
                          />
                        </ListItemButton>
                        {idx < sortedVersions.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, minHeight: 320 }}>
                {selectedVersion ? (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                      {getIcfVersionDisplayLabel(selectedVersion)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      Saved {selectedVersion.createdAt
                        ? new Date(selectedVersion.createdAt).toLocaleString()
                        : '—'}
                      {selectedVersion.globalVersionNumber
                        ? ` · Global v${selectedVersion.globalVersionNumber}`
                        : ''}
                    </Typography>
                    {selectedVersion.output ? (
                      <Box
                        className="icf-preview-content"
                        sx={{
                          border: '1px solid #eee',
                          borderRadius: 1,
                          p: 2,
                          maxHeight: 480,
                          overflow: 'auto',
                          bgcolor: '#fafafa',
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(selectedVersion.output) }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No content stored for this version.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a version to preview its content.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ICFSectionHistory;
